import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { useTelegram } from '@/shared/lib/telegram'
import { ENABLE_MOCK_AUTH, MOCK_USER } from '@/shared/config/dev-mocks'
import type { User, UserStats } from '../model/types'

// ============================================================
// Типы ответов API
// ============================================================

interface UserResponse {
  success: boolean
  data: User
}

interface UserStatsResponse {
  success: boolean
  data: UserStats
}

// ============================================================
// API Hooks
// ============================================================

/**
 * Получить профиль текущего пользователя
 */
export function useUser() {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['user', 'me'],
    queryFn: async () => {
      // В режиме моков возвращаем моковые данные
      if (ENABLE_MOCK_AUTH) {
        return { success: true, data: MOCK_USER } as UserResponse
      }
      
      if (!initData) {
        throw new Error('Требуется авторизация')
      }
      return apiClient<UserResponse>('/api/user/me', { initData })
    },
    enabled: ENABLE_MOCK_AUTH || !!initData,
    staleTime: 10 * 60 * 1000, // 10 минут
  })
}

/**
 * Получить статистику пользователя
 */
export function useUserStats() {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['user', 'stats'],
    queryFn: () => apiClient<UserStatsResponse>('/api/user/me/stats', { initData }),
    enabled: !!initData,
    staleTime: 60 * 1000, // 1 минута
  })
}

/**
 * Обновить профиль пользователя
 */
export function useUpdateUser() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { firstName?: string; lastName?: string }) => {
      if (!initData) throw new Error('Требуется авторизация')
      
      return apiClient<UserResponse>('/api/user/me', {
        method: 'PATCH',
        body: JSON.stringify(data),
        initData,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}
